import React, { useCallback, useEffect, useRef, useState } from 'react'
import { personalChatList, recentChatList } from '../../api/apiHandler';
import io from 'socket.io-client';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Loading } from '../../components/loading/loading';
import { useTranslation } from 'react-i18next';
import '../../i18';
import { showErrorMessage } from '../../common/notification';
import Globals from '../../common/utils';
import { uploadFileToS3 } from '../../common/uploadToS3';

const Chat = () => {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(true);
    const userInfo = useSelector((state) => state.auth.userInfo);
    const [socket, setSocket] = useState(null);
    const [chatList, setChatList] = useState([]);
    const [sender, setSender] = useState([]);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [chatId, setChatId] = useState("");
    const location = useLocation()
    const [receiverId, setReceiverId] = useState(location.state?.user_id || 0);
    const [receiverRole, setReceiverRole] = useState(location.state?.role || "trainer");
    const [pageToken, setPageToken] = useState(0);
    const [hasMounted, setHasMounted] = useState(false);
    const userId = userInfo?.id;
    const chatEndpoint = `${Globals.APP_CHAT_PORT}/user?user_id=${userId}`;
    const chatContainerRef = useRef(null);
    const [cooldown, setCooldown] = useState(false);
    const [searchedChatList, setSearchedChatList] = useState([]);
    const [file, setFile] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalImage, setModalImage] = useState(null);
    const recentChatListRef = useRef(null);
    const [chatListPageToken, setChatListPageToken] = useState(0);
    const [hasFetchedPersonalChat, setHasFetchedPersonalChat] = useState(true);
    const [searchChat, setSearchChat] = useState('');

    // New state for tracking read messages
    const [isUserAtBottom, setIsUserAtBottom] = useState(true);
    const [hasReadLatestMessage, setHasReadLatestMessage] = useState(false);
    const readMessageTimeoutRef = useRef(null);
    const lastBottomStateRef = useRef(true);
    const scrollTimeoutRef = useRef(null);

    const openImageModal = (image) => {
        setModalImage(image);
        setIsModalOpen(true);
    };

    const closeImageModal = () => {
        setIsModalOpen(false);
        setModalImage(null);
    };

    let lastDisplayedDate = null;

    const [isTyping, setIsTyping] = useState(false);
    const [typingUsers, setTypingUsers] = useState([]);
    const [typingTimeout, setTypingTimeout] = useState(null);

    // Function to check if user is at the bottom of chat
    const checkIfAtBottom = useCallback(() => {
        if (chatContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
            const isAtBottom = scrollHeight - scrollTop - clientHeight <= 30; // Reduced threshold

            // Only update state if the value actually changed
            if (lastBottomStateRef.current !== isAtBottom) {
                lastBottomStateRef.current = isAtBottom;
                setIsUserAtBottom(isAtBottom);
            }

            return isAtBottom;
        }
        return false;
    }, []);

    // Function to mark messages as read
    const markMessagesAsRead = useCallback(() => {
        if (socket && sender?.chat_id && receiverId && messages.length > 0) {
            const data = {
                chat_id: sender.chat_id,
                user_id: receiverId
            };
            socket.emit('read_message', data);
            setHasReadLatestMessage(true);

            // Update chat list to remove unread count
            setChatList(prevList =>
                prevList.map(chat =>
                    chat.Receiver_data?.id === receiverId
                        ? { ...chat, unreadCount: 0 }
                        : chat
                )
            );
            setSearchedChatList(prevList =>
                prevList.map(chat =>
                    chat.Receiver_data?.id === receiverId
                        ? { ...chat, unreadCount: 0 }
                        : chat
                )
            );
        }
    }, [socket, sender?.chat_id, receiverId, messages.length]);

    // Enhanced scroll handler for read messages
    const handleScroll = useCallback((e) => {
        // Clear any existing timeout to debounce
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
            const isAtTop = e.target.scrollTop === 0;
            const isAtBottom = checkIfAtBottom();

            // Handle pagination when scrolling to top
            if (!cooldown && isAtTop) {
                setPageToken((prevToken) => prevToken + 1);
            }

            // Handle read messages when user scrolls to bottom
            if (isAtBottom && messages.length > 0 && !hasReadLatestMessage) {
                if (readMessageTimeoutRef.current) {
                    clearTimeout(readMessageTimeoutRef.current);
                }

                readMessageTimeoutRef.current = setTimeout(() => {
                    markMessagesAsRead();
                }, 1000);
            }
        }, 50); // 50ms debounce
    }, [cooldown, checkIfAtBottom, messages.length, hasReadLatestMessage, markMessagesAsRead]);

    // Add this cleanup useEffect
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    // Mark messages as read when chat is opened and user is at bottom
    useEffect(() => {
        if (messages.length > 0 && isUserAtBottom && !hasReadLatestMessage) {
            // Clear any existing timeout
            if (readMessageTimeoutRef.current) {
                clearTimeout(readMessageTimeoutRef.current);
            }

            // Set a delay to mark as read
            readMessageTimeoutRef.current = setTimeout(() => {
                markMessagesAsRead();
            }, 1500); // 1.5 second delay when chat is opened
        }

        return () => {
            if (readMessageTimeoutRef.current) {
                clearTimeout(readMessageTimeoutRef.current);
            }
        };
    }, [messages.length, isUserAtBottom, hasReadLatestMessage, markMessagesAsRead]);

    // Reset read status when switching chats
    useEffect(() => {
        setHasReadLatestMessage(false);
        if (readMessageTimeoutRef.current) {
            clearTimeout(readMessageTimeoutRef.current);
        }
    }, [receiverId, receiverRole]);

    // Auto-scroll to bottom when new messages arrive (only if user was already at bottom)
    useEffect(() => {
        if (messages.length > 0 && isUserAtBottom && chatContainerRef.current) {
            setTimeout(() => {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                checkIfAtBottom();
            }, 100);
        }
    }, [messages.length, isUserAtBottom, checkIfAtBottom]);

    useEffect(() => {
        if (socket) {
            // Listen for 'start_typing' event
            socket.on('start_typing', (data) => {
                setTypingUsers((prev) => {
                    const exists = prev.some(
                        (user) => user.user_id === data.user_id && user.chat_id === data.chat_id
                    );

                    if (!exists) {
                        return [...prev, { user_id: data.user_id, chat_id: data.chat_id }];
                    }
                    return prev;
                });
            });

            socket.on('stop_typing', (data) => {
                setTypingUsers((prev) => {
                    return prev.filter(
                        (user) => !(user.user_id === data.user_id && user.chat_id === data.chat_id)
                    );
                });
            });

            return () => {
                socket.off('start_typing');
                socket.off('stop_typing');
            };
        }
    }, [socket]);

    const fetchChatList = useCallback(async () => {
        if (!hasFetchedPersonalChat) return;
        try {
            setHasFetchedPersonalChat(false);
            const response = await recentChatList({ page_token: chatListPageToken, limit: 8, search: searchChat });
            if (response.code === 200) {
                const sortedChatList = response.data.result.sort((a, b) =>
                    new Date(b.update_datetime) - new Date(a.update_datetime)
                );
                setChatList((prevList) => {
                    const mergedList = [...prevList, ...sortedChatList];
                    const uniqueList = Array.from(
                        new Map(mergedList.map((chat) => [chat.id, chat])).values()
                    );
                    return uniqueList.sort((a, b) => new Date(b.update_datetime) - new Date(a.update_datetime));
                });

                setSearchedChatList((prevList) => {
                    const mergedList = [...prevList, ...sortedChatList];
                    const uniqueList = Array.from(
                        new Map(mergedList.map((chat) => [chat.id, chat])).values()
                    );
                    return uniqueList.sort((a, b) => new Date(b.update_datetime) - new Date(a.update_datetime));
                });

                setChatId(sortedChatList[0].chat_id);
                if (receiverId === 0 && sortedChatList.length > 0) {
                    setReceiverId(sortedChatList[0].Receiver_data?.id);
                    setReceiverRole(sortedChatList[0].Receiver_data?.role);
                    let receiverData = {
                        ...sortedChatList[0].Receiver_data, chat_id: sortedChatList[0].chat_id
                    }
                    setSender(receiverData);
                }
                else if (receiverId !== 0) {
                    // FIXED: Find the correct chat based on receiverId AND receiverRole
                    let receiverData = sortedChatList.find(chat =>
                        chat.Receiver_data.id === receiverId &&
                        chat.Receiver_data.role === receiverRole
                    );

                    if (receiverData) {
                        setChatId(receiverData.chat_id);
                        setSender({
                            ...receiverData.Receiver_data,
                            chat_id: receiverData.chat_id
                        });
                    } else {
                        // If not found in current list, fetch personal chat
                        if (pageToken === 0) await fetchPersonalChatList();
                    }
                }
            }
            else {
                // Handle case when no recent chats found
                if (receiverId !== 0) {
                    let receiverData = searchedChatList?.find(chat =>
                        chat.Receiver_data.id === receiverId &&
                        chat.Receiver_data.role === receiverRole
                    );
                    if (receiverData) {
                        setSender({
                            ...receiverData.Receiver_data,
                            chat_id: receiverData.chat_id
                        });
                    }
                    if (pageToken === 0) await fetchPersonalChatList();
                }
            }
            setHasFetchedPersonalChat(true);
        } catch (error) {
            console.error('Error fetching chat list:', error);
        }
    }, [receiverId, pageToken, chatListPageToken, hasFetchedPersonalChat, receiverRole, searchChat]);

    const fetchPersonalChatList = useCallback(
        async () => {
            try {
                if (receiverId !== 0) {
                    const personalListResponse = await personalChatList({ page_token: pageToken, user_id: receiverId, user_role: receiverRole });
                    if (personalListResponse.code === 200) {
                        if (!chatList.some(chat => chat.user_id === personalListResponse.data.detail.user_id)) {
                            const receiverData = {
                                full_name: personalListResponse.data.detail.full_name,
                                image: personalListResponse.data.detail.image,
                                id: personalListResponse.data.detail.user_id,
                                chat_id: personalListResponse.data.detail.chat_id
                            }
                            setSender(receiverData);
                        }
                        if (personalListResponse.data.list?.length > 0) {
                            const newMessages = personalListResponse?.data?.list.reverse();
                            const filterMsg = prevMessages => {
                                const uniqueNewMessages = newMessages.filter(
                                    msg => !prevMessages.some(existingMsg => existingMsg.id === msg.id)
                                );

                                const allMessages = [...uniqueNewMessages, ...prevMessages];

                                const sortedMessages = allMessages.sort((a, b) => {
                                    const dateA = new Date(a.insert_datetime);
                                    const dateB = new Date(b.insert_datetime);
                                    return dateA - dateB;
                                });

                                return sortedMessages;
                            };
                            setMessages(filterMsg);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching chat list:', error);
            }
        }, [receiverId, pageToken, receiverRole, chatList]
    );

    useEffect(() => {
        if (receiverId !== 0 && !hasFetchedPersonalChat) {
            fetchPersonalChatList();
        }
    }, [receiverId, hasFetchedPersonalChat]);

    useEffect(() => {
        const socketConnection = io(chatEndpoint, {
            query: { user_id: userId },
        });
        setSocket(socketConnection);

        if (!hasMounted) setIsLoading(true);
        const fetchData = async () => {
            try {
                await fetchChatList();
            } catch (error) {
                console.error('Error fetching About Us:', error);
            }
            finally {
                if (!hasMounted) setIsLoading(false);
                setHasMounted(true);
            }
        };

        fetchData();
        socketConnection.on('send_message', async (messageData) => {
            if (messageData.code === '1') {
                const response = await recentChatList({ page_token: 0, limit: 1 });
                const newChat = response.data.result[0];
                if (newChat) {
                    const updatedChatList = [...(searchedChatList || [])];
                    const existingChatIndex = updatedChatList.findIndex(chat => chat.id === newChat.id);

                    if (existingChatIndex !== -1) {
                        const updatedChat = {
                            ...updatedChatList[existingChatIndex],
                            ...newChat,
                        };
                        updatedChatList.splice(existingChatIndex, 1);
                        setChatList([updatedChat, ...updatedChatList]);
                        setSearchedChatList([updatedChat, ...updatedChatList]);
                    } else {
                        setChatList([newChat, ...updatedChatList])
                        setSearchedChatList([newChat, ...updatedChatList])
                    }
                }
                if (sender?.id == messageData?.data?.user_id) {
                    const newMessage = messageData.data;
                    setMessages(prevMessages => {
                        const allMessages = [...prevMessages, newMessage];
                        return allMessages.sort((a, b) => new Date(a.insert_datetime) - new Date(b.insert_datetime));
                    });

                    // Reset read status when new message arrives
                    setHasReadLatestMessage(false);

                    // Auto-scroll if user was at bottom
                    if (isUserAtBottom && chatContainerRef.current) {
                        setTimeout(() => {
                            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                        }, 100);
                    }
                }
            } else {
                console.error('Error receiving message:', messageData.message);
            }
        });

        return () => {
            socketConnection.disconnect();
        };
    }, [chatEndpoint, sender?.id, isUserAtBottom]);

    useEffect(() => {
        setMessages([]);
        setPageToken(0);
        fetchChatList();

        setCooldown(true);
        const cooldownTimeout = setTimeout(() => {
            setCooldown(false);
        }, 500);

        return () => clearTimeout(cooldownTimeout);
    }, [receiverRole, receiverId]);

    useEffect(() => {
        fetchChatList();
    }, [chatListPageToken])

    const handleInputChange = (e) => {
        const message = e.target.value;
        setInputMessage(message);

        if (message.trim()) {
            if (!isTyping) {
                setIsTyping(true);
                socket.emit('start_typing', { user_id: receiverId, chat_id: chatId });
            }

            if (typingTimeout) {
                clearTimeout(typingTimeout);
            }

            setTypingTimeout(
                setTimeout(() => {
                    setIsTyping(false);
                    socket.emit('stop_typing', { user_id: receiverId, chat_id: chatId });
                }, 2000)
            );
        } else if (isTyping) {
            setIsTyping(false);
            socket.emit('stop_typing', { user_id: receiverId, chat_id: chatId });
        }
    };

    useEffect(() => {
        if (isTyping) {
            const timeout = setTimeout(() => {
                setIsTyping(false);
                socket.emit('stop_typing', { user_id: receiverId, chat_id: chatId });
            }, 3000);

            return () => clearTimeout(timeout);
        }
    }, [inputMessage]);

    const TypingIndicator = ({ userId, typingUsers, chatId }) => {
        const isTyping = typingUsers.some(
            (user) =>
                user.user_id == userId &&
                user.chat_id == chatId
        )
        if (!isTyping) return null;

        return (
            <div className="typing-indicator">
                {t('typing')}...
            </div>
        );
    };

    const sendMessage = useCallback(
        async (e) => {
            if (e) e.preventDefault();

            if (isSending) return;

            if ((inputMessage.trim() || file) && socket) {
                setIsSending(true);

                let fileType = "";
                if (file) {
                    const [type, format] = file.type.split("/");

                    if (type === "image") {
                        fileType = "photo";
                    } else {
                        showErrorMessage(t("imagesAllowed"));
                        setIsSending(false);
                        return;
                    }
                }
                let chatFile;

                if (file) {
                    chatFile = await uploadFileToS3(file, "chat_send_image");
                }

                const messageData = {
                    message: inputMessage ? inputMessage.trim() : chatFile?.fileName ?? "",
                    role: "user",
                    type: file ? fileType : "text",
                    chat_id: sender.chat_id,
                };

                const emitSocketMessage = (messageData) => {
                    return new Promise((resolve, reject) => {
                        socket.emit('send_message', JSON.stringify(messageData), (response) => {
                            if (response && response.status === 200) {
                                resolve(response);
                            } else {
                                reject(new Error('Message sending failed'));
                            }
                        });
                    });
                };

                await emitSocketMessage(messageData)
                setPageToken(0);
                const response = await recentChatList({ page_token: 0, limit: 1 });
                const newChat = response.data.result[0];
                if (newChat) {
                    const updatedChatList = [...(chatList || [])];
                    const existingChatIndex = updatedChatList.findIndex(chat => chat.id === newChat.id);

                    if (existingChatIndex !== -1) {
                        const updatedChat = {
                            ...updatedChatList[existingChatIndex],
                            ...newChat,
                        };
                        updatedChatList.splice(existingChatIndex, 1);
                        setChatList([updatedChat, ...updatedChatList]);
                        setSearchedChatList([updatedChat, ...updatedChatList]);
                    } else {
                        setChatList([newChat, ...updatedChatList])
                        setSearchedChatList([newChat, ...updatedChatList])
                    }
                }
                await fetchPersonalChatList();
                setInputMessage('');
                setFile('');
                setIsSending(false);

                // Mark as read since user sent a message (implies they've seen the conversation)
                setHasReadLatestMessage(false);
            }
        },
        [sender, inputMessage, socket, receiverId, file, isSending]
    );

    const formatMessageTime = (time) => {
        const messageTime = moment(time);
        const currentTime = moment();

        if (messageTime.isSame(currentTime, 'day')) {
            return messageTime.format('h:mm A');
        } else if (messageTime.isSame(currentTime.clone().subtract(1, 'day'), 'day')) {
            return `Yesterday`;
        } else {
            return `${messageTime.format('DD/MM/YYYY')}`;
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value.trim();
        setSearchChat(value);
        setSearchedChatList([]);
        setChatListPageToken(0);
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchChatList();
        }, 500)
        return (() => {
            clearTimeout(timeout)
        })
    }, [searchChat])

    const uploadImageEvent = (e) => {
        const file = e.target.files[0];
        if (file) {
            const timestamp = new Date().getTime();
            const newFileName = `${timestamp}_${file?.name.replace(/\s+/g, '_')}`;
            const newFile = new File([file], newFileName, { type: file?.type });
            setFile(newFile);
        }
    };

    const formatFile = (fileName) => {
        if (!fileName.includes('_')) {
            return fileName;
        }

        const formattedName = fileName.substring(fileName.indexOf('_') + 1);
        return formattedName;
    };

    useEffect(() => {
        const prevHeight = chatContainerRef.current?.scrollHeight;

        if (chatContainerRef.current) {
            fetchPersonalChatList().then(() => {
                setTimeout(() => {
                    const newHeight = chatContainerRef.current?.scrollHeight;
                    if (newHeight && prevHeight) {
                        chatContainerRef.current.scrollTop += newHeight - prevHeight;
                    }
                }, 0);
            });
        }
    }, [pageToken]);

    useEffect(() => {
        if (pageToken === 0 && chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            // Check if user is at bottom after scrolling
            setTimeout(() => {
                checkIfAtBottom();
            }, 100);
        }
    }, [pageToken, receiverId, receiverRole, messages, checkIfAtBottom]);

    const handleRecentChatlistScroll = (e) => {
        const isAtBottom = e.target.scrollHeight === e.target.scrollTop + e.target.clientHeight;
        if (isAtBottom) {
            setChatListPageToken((prevToken) => prevToken + 1);
        }
    }

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (readMessageTimeoutRef.current) {
                clearTimeout(readMessageTimeoutRef.current);
            }
        };
    }, []);

    if (isLoading) {
        return <Loading />;
    }

    return (
        <>
            <div className="course-single-area pd-top-120 pd-bottom-90 bgBrown mt-3">
                <form action="#">
                    <div className="container">
                        <div className="row">
                            <div className="col-xxl-4 col-xl-4 col-lg-5 col-md-6 col-sm-12 col-12 customer-left-grid m-0">
                                <div className="coustomer-message-sub">
                                    <div className="coustomer-message-input">
                                        <input
                                            type="text"
                                            className="form-control coustomer-msg-input"
                                            placeholder="Search"
                                            onChange={handleSearch}
                                        />
                                    </div>
                                    <div className="coustomer-chat-main" id="customer-overflow-y"
                                        onScroll={handleRecentChatlistScroll}
                                        ref={recentChatListRef}
                                    >
                                        <ul className="customer-chat-ul">
                                            {searchedChatList && searchedChatList.map((chat, index) => (
                                                <li key={index} className="customer-chat-li">
                                                    <Link onClick={() => {
                                                        setReceiverId(chat?.Receiver_data?.id)
                                                        setReceiverRole(chat?.Receiver_data?.role)
                                                    }}>
                                                        <div className="coustomer-chat-item">
                                                            <div className="coustomer-chat-left">
                                                                <div className="coustomer-chat-avatar">
                                                                    <img
                                                                        src={chat?.Receiver_data?.image || process.env.PUBLIC_URL + "/assets/img/icon/default.jpg"}
                                                                        alt="user-img"
                                                                        className="coustomer-chat-user-profile"
                                                                    />
                                                                </div>
                                                                <div className="coustomer-chat-user-name">
                                                                    <div className="customer-name-time-main">
                                                                        <h6 className="coustomer-chat-heading">
                                                                            {chat?.Receiver_data?.full_name}
                                                                        </h6>
                                                                        <ul className="customer-time-ul">
                                                                            <li className="customer-time-li">
                                                                                {/* {moment(chat.insert_datetime).format('LT')} */}
                                                                                {chat.update_datetime && formatMessageTime(chat.update_datetime)}
                                                                                {chat.unreadCount !== 0 && (
                                                                                    <span className='mx-2'>{chat.unreadCount}</span>
                                                                                )}
                                                                            </li>
                                                                        </ul>
                                                                    </div>
                                                                    {typingUsers.some(
                                                                        (user) =>
                                                                            user.user_id == chat.Receiver_data.id &&
                                                                            user.chat_id == chat.id
                                                                    ) ? (
                                                                        <TypingIndicator userId={chat.Receiver_data.id} chatId={chat.id} typingUsers={typingUsers} />
                                                                    ) : chat.type && chat.type !== "text" ? (
                                                                        <p className="coustomer-chat-text">
                                                                            <img
                                                                                style={{ height: "16px", width: "16px" }}
                                                                                src={
                                                                                    process.env.PUBLIC_URL +
                                                                                    (chat.type === "photo"
                                                                                        ? "/assets/img/icon/Combined-Shape.svg"
                                                                                        : chat.type === "video"
                                                                                            ? "/assets/img/icon/animetionvideo.svg"
                                                                                            : chat.type === "audio"
                                                                                                ? "/assets/img/icon/forms.png"
                                                                                                : "/assets/img/icon/forms.png")
                                                                                }
                                                                            />
                                                                            {" " + chat.type}
                                                                        </p>
                                                                    ) : (
                                                                        <p className="coustomer-chat-text text-truncate-1">
                                                                            {chat.last_message}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                </li>
                                            ))}
                                            {searchedChatList?.length === 0 && (
                                                <div className='text-center'>
                                                    {t('noRecentChats')}
                                                </div>
                                            )}
                                            {/* <TypingIndicator /> */}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xxl-8 col-xl-8 col-lg-7 col-md-6 col-sm-12 col-12 customer-right-grid m-0">
                                <div className="coustomer-chat-person-main mx-2">
                                    <div className="coustomer-chat-person-sub">
                                        <div className="coustomer-chat-person-header">
                                            <div className="coustomer-chat-left">
                                                <div className="coustomer-chat-avatar">
                                                    {sender?.length !== 0 && (
                                                        <img
                                                            src={sender?.image || process.env.PUBLIC_URL + "/assets/img/icon/default.jpg"}
                                                            alt="user-img"
                                                            className="coustomer-chat-user-profile"
                                                        />

                                                    )}
                                                    {/* <span className="coustomer-chat-online" /> */}
                                                </div>
                                                <div className="coustomer-chat-user-name">
                                                    <h6 className="coustomer-chat-heading">{sender?.full_name}</h6>
                                                    {typingUsers.some(
                                                        (user) =>
                                                            user.user_id == sender.id &&
                                                            user.chat_id == sender.chat_id) && (
                                                            <TypingIndicator userId={sender.id} chatId={sender.chat_id} typingUsers={typingUsers} />)}
                                                    {/* <p className="coustomer-chat-text">Last seen at 04:30 PM</p> */}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="coustomer-chat-person-body" id="customer-overflow-y"
                                            onScroll={handleScroll}
                                            ref={chatContainerRef}
                                        >
                                            <ul className="cuatomer-right-ul">
                                                {messages?.length > 0 && messages.map((message, index) => {
                                                    const messageDate = moment(message.insert_datetime).format('DD/MM/YYYY');
                                                    const isNewDate = messageDate !== lastDisplayedDate;
                                                    lastDisplayedDate = messageDate;
                                                    return (
                                                        <React.Fragment key={index}>
                                                            {isNewDate && (
                                                                <li className="coustomer-chat-date-separator">
                                                                    <p className="coustomer-chat-date text-center">
                                                                        {messageDate === moment().format('DD/MM/YYYY')
                                                                            ? t('Today')
                                                                            : messageDate === moment().subtract(1, 'day').format('DD/MM/YYYY')
                                                                                ? t('Yesterday')
                                                                                : messageDate}
                                                                    </p>
                                                                </li>
                                                            )}
                                                            <li className="cuatomer-right-li">
                                                                {message.role === "user" ? (
                                                                    <div className="coustomer-chat-person-message-main-right">
                                                                        <p className={`${message.type === "photo" ? "coustomer-chat-person-message-blue-image" : "coustomer-chat-person-message-blue"} mt-3`}>
                                                                            {message.type === "photo" &&
                                                                                <img
                                                                                    src={`${message.message}`}
                                                                                    width={250}
                                                                                    height={140.63}
                                                                                    style={{ objectFit: "cover" }}
                                                                                    onClick={() => openImageModal(message.message)}
                                                                                    alt="Chat Photo"
                                                                                />
                                                                            }
                                                                            {message.type === "text" && message.message}
                                                                        </p>
                                                                        <p className="coustomer-chat-person-message-time">
                                                                            {moment(message.insert_datetime).format('h:mm A')}
                                                                        </p>
                                                                    </div>
                                                                ) : (
                                                                    <div className="coustomer-chat-person-message-main-left">
                                                                        <p className={`${message.type === "photo" ? "coustomer-chat-person-message-blue-left-image" : "coustomer-chat-person-message-blue-left"} mt-3`}>
                                                                            {message.type === "photo" &&
                                                                                <img
                                                                                    src={`${message.message}`}
                                                                                    width={250}
                                                                                    height={140.63}
                                                                                    style={{ objectFit: "cover" }}
                                                                                    onClick={() => openImageModal(message.message)}
                                                                                    alt="Chat Photo"
                                                                                />
                                                                            }
                                                                            {message.type === "text" && message.message}
                                                                        </p>
                                                                        <p className="coustomer-chat-person-message-time mt-2">
                                                                            {moment(message.insert_datetime).format('h:mm A')}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </li>
                                                        </React.Fragment>
                                                    );
                                                })}
                                                {messages?.length === 0 && (
                                                    <div className='text-center'>
                                                        {t('noMessagesFound')}
                                                    </div>
                                                )}
                                            </ul>
                                        </div>
                                        <div className="coustomer-chat-person-footer">
                                            <div className="coustomer-chat-person-footer-left">
                                                <div className="input-group single-input-inner style-bg-border m-0">
                                                    <span className="input-group-text">
                                                        <img
                                                            src={process.env.PUBLIC_URL + "/assets/img/icon/pin.svg"}
                                                            alt="apply-emoji"
                                                            className="apply-emoji"
                                                        />
                                                        <input
                                                            type="file"
                                                            className="ChatInputFile"
                                                            accept="image/*"
                                                            onChange={(e) => uploadImageEvent(e)}
                                                        />
                                                    </span>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Message"
                                                        value={inputMessage || (file ? formatFile(file.name) : '')}
                                                        onChange={handleInputChange}
                                                        disabled={!!file.name}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault(); // Prevent form submission or default behavior
                                                                sendMessage(); // Trigger the sendMessage function
                                                            }
                                                        }}
                                                    />
                                                    <a
                                                        href="javascript:void(0)"
                                                        className="input-group-text"
                                                        onClick={sendMessage}
                                                    >
                                                        <img
                                                            src={process.env.PUBLIC_URL + "/assets/img/icon/SendMessageIcon.svg"}
                                                            alt="apply-emoji"
                                                            className="apply-emoji"
                                                        />
                                                    </a>
                                                </div>
                                                {/* <a href="#" className="chatboatIconLink"
                                                    onClick={(e) => e.preventDefault()}
                                                >
                                                    <img
                                                        src={process.env.PUBLIC_URL + "/assets/img/icon/chatboatIcon.svg"}
                                                        alt="chatboatIcon"
                                                        className="chatboatIcon imgCover"
                                                    />
                                                </a> */}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
                {isModalOpen && (
                    <div className='d-flex gap-3 flex-column'
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(0, 0, 0, 0.8)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 1000,
                        }}
                        onClick={closeImageModal}
                    >
                        <img
                            src={modalImage}
                            style={{
                                maxWidth: "90%",
                                maxHeight: "90%",
                                boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.7)",
                                borderRadius: "8px",
                            }}
                            alt="Full View"
                            onClick={(e) => e.stopPropagation()}
                        />
                        {/* <button
                            style={{
                                padding: "10px 20px",
                                backgroundColor: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "5px",
                                cursor: "pointer",
                            }}
                            onClick={downloadImage}
                        >
                            Download
                        </button> */}
                        <button
                            style={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                background: "transparent",
                                border: "none",
                                color: "white",
                                fontSize: "24px",
                                cursor: "pointer",
                            }}
                            onClick={closeImageModal}
                        >
                            &times;
                        </button>
                    </div>
                )}
            </div>
        </>
    )
}

export default Chat;